import React, { useState, useEffect } from "react";
import { Container, Navbar, Button, Spinner, Col, Card } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { HiExternalLink } from "react-icons/hi";
import { FaCheckCircle } from "react-icons/fa";
import { ImRadioChecked2 } from "react-icons/im";
import { toast } from "react-toastify";
import axios from "axios";

import CmsEditBanner from "../helpers/cms/CmsEditBanner";
import { useCmsHook } from "../helpers/cms/cmsHook";
import { cmsConstructor } from "../helpers/cms/cmsConstructor";

import { getUrl } from "../helpers/url";

function LogsIframe() {
  const { cmsInstance, setCmsInstance } = useCmsHook();
  const data = {
    triggered: {
      label: "stage1",
      value: undefined,
    },
    findCreateCustomer: {
      label: "stage2",
      value: undefined,
    },
    findCreateProducts: {
      label: "stage3",
      value: undefined,
    },
    syncInvoice: {
      label: "stage4",
      value: undefined,
    },
  };
  const [stageData, setStageData] = useState(data);
  const [isLoading, setIsLoading] = useState(false);
  const [dealStage, setDealStage] = useState("");
  const [retriggeredNote, setRetriggeredNote] = useState("");
  const [searchParams] = useSearchParams();

  const renderIcons = (value) => {
    if (value === true) {
      return <FaCheckCircle size={25} className="me-3 logs-success-icon" />;
    } else if (value === false) {
      return <ImRadioChecked2 size={25} className="me-3 logs-failed-icon" />;
    } else {
      return <div className="logs-empty-icon me-3"></div>;
    }
  };

  /**
   * @description This function is responsible to retrigger the flow
   */
  const onRetriggerFlow = async () => {
    try {
      setRetriggeredNote("");
      setIsLoading(true);
      const account = searchParams.get("account");
      const invoiceId = searchParams.get("invoiceId");

      const { data } = await axios.post(getUrl("REACT_APP_RETRIGGER"), {
        account,
        invoiceId,
        eventType: null,
      });

      for (let stage in stageData) {
        stageData[stage].value = true;
      }
      setStageData({ ...stageData });
      setDealStage("");

      let successMsg = "";
      if (data?.msg === "Business central invoice already generated !!") {
        successMsg = "The Business Central draft invoice has already generated";
      } else {
        successMsg =
          "The Business Central draft invoice has been generated successfully";
      }

      setRetriggeredNote(successMsg);

      toast.success(
        cmsInstance &&
          cmsInstance.sendData().cmsContent.notifications.retrigger
            .successContent.retriggerSuccess.message
      );
    } catch (error) {
      console.log(error);
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      setRetriggeredNote(
        `The workflow execution failed due to this error : ${errorMessage}`
      );
      setDealStage(error.response.data.errorStage);
      toast.error(
        cmsInstance &&
          cmsInstance.sendData().cmsContent.notifications.retrigger.failContent
            .retriggerFailed.message
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const stageName = searchParams.get("stageName");
    setDealStage(stageName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dealStage) {
      for (let stage in stageData) {
        stageData[stage].value = undefined;
      }
      setStageData({ ...stageData });

      for (let stage in stageData) {
        if (stage === dealStage) {
          stageData[stage].value = false;
          break;
        } else {
          stageData[stage].value = true;
        }
      }
    }
    setStageData({ ...stageData });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealStage]);

  useEffect(() => {
    const account = searchParams.get("account");
    const invoiceId = searchParams.get("invoiceId");

    if (account && invoiceId) {
      (async () => {
        const { data: stripeInvoice } = await axios.get(
          `${process.env.REACT_APP_STRIPE_URL}/v1/invoices/${invoiceId}`,
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_STRIPE_ACCESS_TOKEN}`,
              "Stripe-Account": account,
            },
          }
        );

        const regex = /"(.*?)"/g;
        const matches =
          stripeInvoice?.metadata?.["Business Central error log"].match(regex);

        if (matches) {
          const wordsInsideQuotes = matches.map((match) => match.slice(1, -1));
          setRetriggeredNote(
            `The workflow execution was failed due to this error : ${wordsInsideQuotes[0]}`
          );
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCmsInstance = async () => {
      const cmsObj = await cmsConstructor();
      setCmsInstance(cmsObj);
    };
    fetchCmsInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Navbar
        fixed="top"
        className="navbar d-flex flex-row justify-content-center"
      >
        <div className="text-center">
          <strong className="navbar-heading">Stripe - Business Central</strong>
          <br></br>
          <span className="navbar-subheading">
            Cloudify -
            <a
              href="https://www.cloudify.biz/contact"
              target="_blank"
              rel="noreferrer"
              className="navbar-subheading-link"
            >
              {" "}
              contact us
              <HiExternalLink />
            </a>
          </span>
        </div>
      </Navbar>

      <div className="page-bg-container pt-5 d-flex flex-column justify-content-center">
        <div className="d-flex flex-row justify-content-center">
          <CmsEditBanner />
        </div>
        <Container
          style={{
            backgroundColor: "#ffffff",
          }}
          className="window-bg-container px-3 py-5 d-flex flex-column align-items-center"
        >
          <Col md={8} className="mb-4">
            {cmsInstance &&
              cmsInstance.showRichText(
                "cmsContent.retrigger.description",
                "rules-description text-center mb-0"
              )}
          </Col>

          <Col md={6} className="d-flex flex-column align-items-center w-100">
            <Col md={6} className="mb-4">
              {retriggeredNote && (
                <Card>
                  <Card.Body>
                    <p className="my-1">
                      <b>Note: </b> <br />
                      {retriggeredNote}
                    </p>
                  </Card.Body>
                </Card>
              )}
            </Col>

            <div>
              {stageData &&
                Object.keys(stageData).length > 0 &&
                Object.keys(stageData).map((stage) => (
                  <div key={stage}>
                    <div className="d-flex flex-row align-items-center">
                      {renderIcons(stageData[stage].value)}
                      {/* <b className="rules-description">
                        {stageData[stage].label}
                      </b> */}
                      {cmsInstance &&
                        cmsInstance.showRichText(
                          `cmsContent.retrigger.${stageData[stage].label}`,
                          "retrigger-label"
                        )}
                    </div>
                    {stage !== "syncInvoice" && (
                      <div
                        style={{
                          borderLeft: `${
                            stageData[stage].value === true
                              ? "4px solid #08a742"
                              : "4px solid #d9d9d9"
                          }`,
                          height: "30px",
                          marginLeft: "10px",
                        }}
                      ></div>
                    )}
                  </div>
                ))}
              <div className="d-flex flex-row justify-content-center">
                <Button
                  className="mt-5 save-changes-button"
                  onClick={() => onRetriggerFlow()}
                  disabled={isLoading || !dealStage}
                >
                  {isLoading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <span>Replay</span>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Container>
      </div>
    </div>
  );
}

export default LogsIframe;
